import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface FeatureUnlockedModalProps {
  isOpen: boolean;
  onClose: () => void;
  featureName: 'advancedTasks' | 'advancedActivities';
}

export default function FeatureUnlockedModal({ isOpen, onClose, featureName }: FeatureUnlockedModalProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  if (!isOpen) return null;

  const isTasksFeature = featureName === 'advancedTasks';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-md shadow-2xl animate-[fadeIn_0.3s_ease-out]">
        {/* Celebración */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 dark:from-emerald-500/10 dark:to-teal-500/10"></div>
          <div className="relative p-6 text-center">
            <div className="text-6xl mb-3 animate-bounce">🎉</div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {t('featureUnlocked.congratulations')}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('featureUnlocked.youUnlocked')}{' '}
              <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                {isTasksFeature ? t('featureUnlocked.advancedTasks') : t('featureUnlocked.advancedActivities')}
              </span>
            </p>
          </div>
        </div>

        {/* Contenido */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-800">
          <div className="mb-6">
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              {t('featureUnlocked.nowYouCanAccess')}
            </p>
            <ul className="space-y-2">
              {isTasksFeature ? (
                <>
                  <li className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <span className="text-emerald-500 mt-0.5">✓</span>
                    <span><strong>{t('featureUnlocked.customSchedules')}:</strong> {t('featureUnlocked.customSchedulesDesc')}</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <span className="text-emerald-500 mt-0.5">✓</span>
                    <span><strong>{t('featureUnlocked.endDates')}:</strong> {t('featureUnlocked.endDatesDesc')}</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <span className="text-emerald-500 mt-0.5">✓</span>
                    <span><strong>{t('featureUnlocked.aiSuggestions')}:</strong> {t('featureUnlocked.aiSuggestionsDesc')}</span>
                  </li>
                </>
              ) : (
                <>
                  <li className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <span className="text-emerald-500 mt-0.5">✓</span>
                    <span><strong>{t('featureUnlocked.durationGrace')}:</strong> {t('featureUnlocked.durationGraceDesc')}</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <span className="text-emerald-500 mt-0.5">✓</span>
                    <span><strong>{t('featureUnlocked.exchangeSystem')}:</strong> {t('featureUnlocked.exchangeSystemDesc')}</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <span className="text-emerald-500 mt-0.5">✓</span>
                    <span><strong>{t('featureUnlocked.advancedPolicies')}:</strong> {t('featureUnlocked.advancedPoliciesDesc')}</span>
                  </li>
                </>
              )}
            </ul>
          </div>

          {/* Banner de configuración de horarios */}
          <div className="mb-6 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-700">
            <div className="flex items-start gap-3">
              <span className="text-2xl">⏰</span>
              <div className="flex-1">
                <p className="text-sm font-semibold text-purple-900 dark:text-purple-100 mb-1">
                  {t('featureUnlocked.configureSchedules')}
                </p>
                <p className="text-xs text-purple-700 dark:text-purple-300 mb-2">
                  {t('featureUnlocked.configureSchedulesDesc')}
                </p>
                <button 
                  onClick={() => {
                    onClose();
                    navigate('/groups/groups');
                  }}
                  className="text-xs text-purple-600 dark:text-purple-400 hover:underline font-medium"
                >
                  {t('featureUnlocked.configureNow')}
                </button>
              </div>
            </div>
          </div>

          {/* Acciones */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              {t('common.close')}
            </button>
            <button
              onClick={() => {
                onClose();
                navigate('/groups/home-groups');
              }}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-emerald-500 rounded-lg hover:bg-emerald-600 transition-colors"
            >
              {t('featureUnlocked.viewBoard')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
